import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { Icon } from './Icon';
import JellyfishLoader from './JellyfishLoader';
import './NotificationDropdown.css';

const NotificationDropdown = ({ isOpen, onClose, onNotificationClick }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingFollows, setPendingFollows] = useState({}); // Map of follower_id -> follow record
  const [processingFollows, setProcessingFollows] = useState({}); // Track loading state
  const navigate = useNavigate();

  // Click outside to close functionality
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.notification-dropdown')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      fetchPendingFollows();
    }
  }, [isOpen]);

  useEffect(() => {
    // Set up real-time subscription for new notifications
    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'notifications' 
        }, 
        (payload) => {
          if (payload.new && payload.new.type !== 'message') {
            setNotifications(prev => [payload.new, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .on('postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          if (payload.new) {
            // Only handle non-message notifications in the dropdown
            if (payload.new.type !== 'message') {
              setNotifications(prev => 
                prev.map(n => n.id === payload.new.id ? payload.new : n)
              );
              updateUnreadCount();
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Deduplicate requests
      const cacheKey = getCacheKey('NotificationDropdown.fetchNotifications', user.id);
      const data = await dedupeRequest(cacheKey, async () => {
        // Try to select with related_user first, fallback to basic select if it fails
        let data, error;
        
        try {
          const result = await supabase
            .from('notifications')
            .select(`
              *,
              related_user:user_profiles!notifications_related_user_id_fkey(
                user_id,
                profile_picture_url,
                username
              )
            `)
            .eq('user_id', user.id)
            .neq('type', 'message') // Exclude message notifications
            .order('created_at', { ascending: false })
            .limit(20);
          
          data = result.data;
          error = result.error;
        } catch (selectError) {
          // Fallback to basic select if related_user join fails
          const result = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .neq('type', 'message') // Exclude message notifications
            .order('created_at', { ascending: false })
            .limit(20);
          
          data = result.data;
          error = result.error;
        }

        if (error) throw error;
        return data || [];
      });

      setNotifications(data);
      updateUnreadCount();
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending follow requests for the current user
  const fetchPendingFollows = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const cacheKey = getCacheKey('fetchPendingFollows', user.id);
      const data = await dedupeRequest(cacheKey, async () => {
        const { data, error } = await supabase
          .from('follows')
          .select('follower_id, followee_id, accepted_at')
          .eq('followee_id', user.id)
          .is('accepted_at', null);

        if (error) throw error;
        return data || [];
      });

      const pendingMap = {};
      (data || []).forEach(follow => {
        pendingMap[follow.follower_id] = follow;
      });
      setPendingFollows(pendingMap);
    } catch (error) {
      console.error('Error fetching pending follows:', error);
    }
  };

  // Accept a follow request
  const handleAcceptFollow = async (e, followerId) => {
    e.stopPropagation(); // Prevent navigation
    
    try {
      setProcessingFollows(prev => ({ ...prev, [followerId]: true }));
      
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { error } = await supabase
        .from('follows')
        .update({ accepted_at: new Date().toISOString() })
        .eq('follower_id', followerId)
        .eq('followee_id', user.id)
        .is('accepted_at', null);

      if (error) throw error;

      // Update local state
      setPendingFollows(prev => {
        const updated = { ...prev };
        delete updated[followerId];
        return updated;
      });

      // Mark notification as read
      const followNotification = notifications.find(
        n => n.type === 'follow' && n.related_user_id === followerId
      );
      if (followNotification) {
        await markAsRead(followNotification.id);
      }
    } catch (error) {
      console.error('Error accepting follow request:', error);
      alert('Failed to accept follow request. Please try again.');
    } finally {
      setProcessingFollows(prev => {
        const updated = { ...prev };
        delete updated[followerId];
        return updated;
      });
    }
  };

  // Decline/Delete a follow request
  const handleDeclineFollow = async (e, followerId) => {
    e.stopPropagation(); // Prevent navigation
    
    try {
      setProcessingFollows(prev => ({ ...prev, [followerId]: true }));
      
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { error } = await supabase
        .from('follows')
        .delete()
        .eq('follower_id', followerId)
        .eq('followee_id', user.id)
        .is('accepted_at', null);

      if (error) throw error;

      // Update local state
      setPendingFollows(prev => {
        const updated = { ...prev };
        delete updated[followerId];
        return updated;
      });

      // Remove notification or mark as read
      const followNotification = notifications.find(
        n => n.type === 'follow' && n.related_user_id === followerId
      );
      if (followNotification) {
        await markAsRead(followNotification.id);
      }
    } catch (error) {
      console.error('Error declining follow request:', error);
      alert('Failed to decline follow request. Please try again.');
    } finally {
      setProcessingFollows(prev => {
        const updated = { ...prev };
        delete updated[followerId];
        return updated;
      });
    }
  };

  const updateUnreadCount = async () => {
    try {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      // Deduplicate requests
      const cacheKey = getCacheKey('NotificationDropdown.updateUnreadCount', user.id);
      const count = await dedupeRequest(cacheKey, async () => {
        const { count } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .neq('type', 'message') // Exclude message notifications
          .eq('is_read', false);
        return count || 0;
      });

      setUnreadCount(count);
    } catch (error) {
      console.error('Error updating unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
      
      // Only update local state for non-message notifications
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Use getSession() to avoid network requests
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .neq('type', 'message') // Exclude message notifications
        .eq('is_read', false);

      if (error) throw error;
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'follow':
        navigate(`/user/${notification.related_id}`);
        break;
      case 'like_post':
      case 'comment_post':
      case 'share_post':
        // Navigate to home page since individual post pages don't exist yet
        navigate('/home');
        break;
      case 'like_comment':
        // Navigate to home page since individual post pages don't exist yet
        navigate('/home');
        break;
      default:
        break;
    }

    onNotificationClick && onNotificationClick(notification);
    onClose();
  };

  const getNotificationText = (notification) => {
    // Handle both old and new notification structures
    let username = 'Someone';
    
    if (notification.related_user?.username) {
      username = notification.related_user.username;
    } else if (notification.related_user_id) {
      // If we have related_user_id but no join data, try to get username from user_profiles table
      // This is a fallback for when the join doesn't work
      username = 'Someone';
    }
    
    switch (notification.type) {
      case 'follow':
        // Check if this is a pending follow request
        const isPending = notification.related_user_id && pendingFollows[notification.related_user_id];
        return isPending 
          ? `${username} wants to follow you`
          : `${username} started following you`;
      case 'like_post':
        return `${username} liked your post`;
      case 'comment_post':
        return `${username} commented on your post`;
      case 'like_comment':
        return `${username} liked your comment`;
      case 'share_post':
        return `${username} reposted your post`;
      case 'content_under_review':
        return getContentUnderReviewText(notification);
      default:
        return 'You have a new notification';
    }
  };

  // Check if a follow notification is pending approval
  const isFollowPending = (notification) => {
    if (notification.type !== 'follow' || !notification.related_user_id) {
      return false;
    }
    return !!pendingFollows[notification.related_user_id];
  };

  const getContentUnderReviewText = (notification) => {
    // Determine content type from the related_id prefix
    if (notification.related_id) {
      if (notification.related_id.startsWith('post:')) {
        return 'Your post is under review';
      } else if (notification.related_id.startsWith('comment:')) {
        return 'Your comment is under review';
      } else if (notification.related_id.startsWith('message:')) {
        return 'Your message is under review';
      }
    }
    
    // Fallback to generic message
    return 'Your content is under review';
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'follow':
        return 'UserPlus';
      case 'like_post':
      case 'like_comment':
        return 'Heart';
      case 'comment_post':
        return 'MessageSquare';
      case 'share_post':
        return 'Repeat';
      case 'content_under_review':
        return 'AlertTriangle';
      default:
        return 'Bell';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffInSeconds = Math.floor((now - created) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="notification-dropdown-backdrop" onClick={onClose}></div>
      <div className="notification-dropdown">
        <div className="notification-header">
          <div className="notification-title">Notifications</div>
          <div className="notification-actions">
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={markAllAsRead}
              >
                Mark all read
              </button>
            )}
            <button 
              className="close-btn"
              onClick={onClose}
            >
              <Icon name="X" className="icon--sm" />
            </button>
          </div>
        </div>
        
        <div className="notification-content">
          {loading ? (
            <div className="notification-loading">
              <JellyfishLoader />
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <div className="empty-text">
                <div className="empty-title">No notifications</div>
                <div className="empty-subtitle">We'll notify you when something happens</div>
              </div>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map(notification => {
                const isPending = isFollowPending(notification);
                const followerId = notification.related_user_id;
                const isProcessing = processingFollows[followerId];
                
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={() => !isPending && handleNotificationClick(notification)}
                  >
                    <div className="notification-icon-container">
                      <Icon 
                        name={getNotificationIcon(notification.type)} 
                        className="notification-icon" 
                      />
                    </div>
                    
                    <div className="notification-details">
                      <div className="notification-text">
                        {getNotificationText(notification)}
                      </div>
                      <div className="notification-time">
                        {formatTimeAgo(notification.created_at)}
                      </div>
                    </div>
                    
                    {isPending ? (
                      <div className="notification-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="notification-accept-btn"
                          onClick={(e) => handleAcceptFollow(e, followerId)}
                          disabled={isProcessing}
                          title="Accept follow request"
                        >
                          {isProcessing ? '...' : 'Accept'}
                        </button>
                        <button
                          className="notification-decline-btn"
                          onClick={(e) => handleDeclineFollow(e, followerId)}
                          disabled={isProcessing}
                          title="Decline follow request"
                        >
                          {isProcessing ? '...' : 'Decline'}
                        </button>
                      </div>
                    ) : (
                      !notification.is_read && (
                        <div className="unread-indicator" />
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationDropdown;
